/**
 * Mirrors the two source-detail routes on `services/engine/src/api/routes/pipelines.ts`.
 *
 * Separate from `pipelines.ts` because it has a different reason to change: that
 * file follows lead and stage CRUD, this one follows the adapter contract's
 * `describeLead`. It also has a different failure posture — a source with
 * nothing to describe is a *normal* answer here, so `not_supported` is a value
 * the caller reads rather than an error it has to catch.
 */

import { z } from 'zod'
import type { LeadDetail, LeadId, PipelineId } from '@ludus/core'
import { apiClient } from './client.ts'
import { classifySourceError, SourceApiError } from './sources.ts'

const mediaSchema = z.object({
  id: z.string(),
  label: z.string().optional(),
  fileName: z.string().nullable().optional(),
  contentType: z.string().nullable().optional(),
  byteSize: z.number().nullable().optional(),
})

const rowSchema = z.object({
  label: z.string(),
  value: z.string(),
  mono: z.boolean().optional(),
})

const linkSchema = z.object({
  label: z.string(),
  url: z.string(),
  hint: z.string().optional(),
})

/**
 * Sections nest, so the schema has to. Zod needs the explicit annotation and the
 * getter to tie the knot — without them the type is inferred as `any` and the
 * whole point of parsing at the boundary is lost.
 */
const sectionSchema: z.ZodType<{
  key: string
  title: string
  subtitle?: string | null
  text?: string
  rows?: Array<z.infer<typeof rowSchema>>
  media?: Array<z.infer<typeof mediaSchema>>
  links?: Array<z.infer<typeof linkSchema>>
  sections?: unknown[]
}> = z.object({
  key: z.string(),
  title: z.string(),
  subtitle: z.string().nullable().optional(),
  text: z.string().optional(),
  rows: z.array(rowSchema).optional(),
  media: z.array(mediaSchema).optional(),
  // Every field the contract carries has to be declared: zod strips what it does
  // not know, so an omission here is invisible — the adapter sends it, the
  // parse quietly drops it, and the UI simply never shows it.
  links: z.array(linkSchema).optional(),
  get sections() {
    return z.array(sectionSchema).optional()
  },
})

const detailSchema = z.object({
  sections: z.array(sectionSchema),
  embedUrl: z.string().nullable().optional(),
})

/**
 * `null` is the honest answer for three different situations that mean the same
 * thing to the page: the adapter does not implement `describeLead`, the lead was
 * typed in by hand, or the pipeline mirrors nothing. In all three there is no
 * source document, and the detail page renders one panel fewer.
 *
 * A source that is *down* is not one of them — that stays an error, because it
 * is a condition that resolves and an operator should see why the panel that was
 * there yesterday is missing today.
 */
export async function fetchLeadDetail(
  pipelineId: PipelineId,
  leadId: LeadId,
): Promise<LeadDetail | null> {
  try {
    const { data } = await apiClient.get(`/pipelines/${pipelineId}/leads/${leadId}/detail`)
    return detailSchema.parse(data) as LeadDetail
  } catch (err) {
    const classified = classifySourceError(err)
    if (classified.kind === 'not_found') return null
    // 501 — the adapter answered, and its answer is "I have nothing to add".
    if (classified.status === 501) return null
    throw classified
  }
}

/**
 * One file's bytes as an object URL. The bearer cannot ride on an `<img src>`,
 * so the image is fetched as a blob and handed to the DOM as a `blob:` URL —
 * which is also what keeps the source's own credential server-side.
 *
 * **The caller owns the URL** and must `URL.revokeObjectURL` it when the element
 * unmounts or the id changes; a blob held by nothing still holds its bytes.
 */
export async function fetchLeadMediaObjectUrl(
  pipelineId: PipelineId,
  leadId: LeadId,
  mediaId: string,
  maxWidth?: number,
): Promise<string> {
  try {
    const { data } = await apiClient.get<Blob>(
      `/pipelines/${pipelineId}/leads/${leadId}/media/${encodeURIComponent(mediaId)}`,
      { responseType: 'blob', params: maxWidth === undefined ? undefined : { w: maxWidth } },
    )
    return URL.createObjectURL(data)
  } catch (err) {
    throw classifySourceError(err)
  }
}

export { SourceApiError }
